import { HomepageHeroProps } from 'components/HomepageHero';
import { findContentfulFlagValue } from 'components/PageSections/utils';
import { IGatsbyImage } from 'utils/types';

import { CardProps } from './Card';

type parseCardsParams = {
  cards: any;
  mode?: 'desktop' | 'mobile';
  titleTag?: string | null;
  isBlogSection?: boolean;
};

const parseCards = ({
  cards,
  titleTag,
  mode = 'desktop',
  isBlogSection,
}: parseCardsParams) => {
  if (!cards) return [];

  return cards?.map((card: Queries.Maybe<Queries.ContentfulSection001>) => {
    const image =
      mode === 'mobile' && isBlogSection
        ? card?.images?.[0]?.mobileImage
        : card?.images?.[0]?.desktopImage;

    const backgroundImage =
      card?.images && card.images.length > 0
        ? image ?? card.images[0]?.desktopImage
        : undefined;

    const productMedia =
      card?.images && card.images.length > 0 ? card.images[0] : undefined;

    // Using mobile image to keep backwards compatibility
    const productImage = productMedia?.mobileImage;
    const productImageAltText =
      productMedia?.mobileImageAltText || productMedia?.desktopImageAltText;

    const cardItem: CardProps = {
      title: card?.metadata?.title ?? '',
      cta: {
        label: card?.metadata?.ctas?.[0]?.label ?? '',
        url: card?.metadata?.ctas?.[0]?.url ?? '',
      },
      backgroundImage: backgroundImage as IGatsbyImage | undefined,
      productImage: productImage as IGatsbyImage | undefined,
      productImageAltText: productImageAltText,
      textColor: card?.metadata?.theme?.textColor ?? '',
      // Hack to know this component is the first one of the page and eager load the images of the carousel
      imageLoadingStrategy: titleTag === 'h1' ? 'eager' : 'lazy',
    };

    return cardItem;
  });
};

export const parseHomepageHero = (
  section: Queries.ContentfulSection001,
): HomepageHeroProps => {
  const { subsections, metadata, eyebrow } = section || [];

  const isBlogSection = findContentfulFlagValue(
    'isBlogSection',
    metadata?.flags,
  );

  const desktopCards = parseCards({
    cards: subsections,
    titleTag: metadata?.context?.titleTag,
    mode: 'desktop',
    isBlogSection,
  });
  const mobileCards = parseCards({
    cards: subsections,
    titleTag: metadata?.context?.titleTag,
    mode: 'mobile',
    isBlogSection,
  });

  const autoScroll =
    findContentfulFlagValue('Auto Scroll', metadata?.flags) === false
      ? 'off'
      : 'on';

  const hasStraightCarousel = findContentfulFlagValue(
    'hasStraightCarousel',
    metadata?.flags,
  );

  return {
    title: metadata?.title,
    subtitle: metadata?.description?.description,
    backgroundColor: metadata?.theme?.backgroundColor,
    titleTag: metadata?.context?.titleTag,
    displayMode: hasStraightCarousel ? 'straight' : 'default',
    desktopCards,
    mobileCards,
    autoScroll,
    eyebrow,
  };
};
